import {
  ChangeDetectionStrategy,
  Component,
  computed,
  DestroyRef,
  effect,
  inject,
  OnDestroy,
  OnInit,
  signal,
  TemplateRef,
  viewChild,
} from '@angular/core';
import {
  FormArray,
  FormBuilder,
  FormGroup,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { TranslocoPipe, TranslocoService } from '@jsverse/transloco';
import {
  delay,
  finalize,
  of,
  Subject,
  switchMap,
  takeUntil,
  timer,
} from 'rxjs';
import { GroupedButtonComponent } from './grouped-button/grouped-button.component';
import { SideMenuAssigneeDropdownViewComponent } from './side-menu-assignee-dropdown-view/side-menu-assignee-dropdown-view.component';
import { RequestComponentService } from './services/request-component/request-component.service';
import { requestDetailsEmptyData } from './request.mockdata';
import { ApproveModalComponent } from './request-actions-modals/approve-modal/approve-modal.component';
import { ReassignModalComponent } from './request-actions-modals/reassign-modal/reassign-modal.component';
import { RejectModalComponent } from './request-actions-modals/reject-modal/reject-modal.component';
import { RejectSendBackModalComponent } from './request-actions-modals/reject-send-back-modal/reject-send-back-modal.component';
import { RejectSendForwardModalComponent } from './request-actions-modals/reject-send-forward-modal/reject-send-forward-modal.component';
import { HoldResumeModalComponent } from './request-actions-modals/hold-resume-modal/hold-resume-modal.component';
import { DiscussionBase } from './base/discussion-base';
import {
  AccessKeyResponse,
  ApiResponse,
  AppPermissions,
  AppStorage,
  EMPLOYEE_ROUTES,
  FEATURE_FLAGS,
  FeatureFlagsService,
  Option,
  OverlayLoaderComponent,
  PermissionsService,
  RequestActionEnum,
  RequestDetailsHeaderComponent,
  RequestDetailsResponse,
  RequestTypeEnum,
  ScrollToErrorDirective,
  StepStatusEnum,
  UPLOAD_FILES_URL_KEY,
  uploadFileUrls,
  UserList,
  UserType,
  WorkflowAccessKeyService,
} from '../../../common';
import { ChevronDownIconComponent } from '@kuiper/icons';
import { ActionClassPipe } from './action-class/action-class.pipe';
import { CollaboratorService } from './services/collaborator/collaborator.service';
import { RequestViewActionsComponent } from './request-view-actions/request-view-actions.component';
import {
  AlertData,
  AlertInlineComponent,
  AssigneeTypePipe,
  ButtonComponent,
  ModalServiceService,
  StepperComponent,
  StepperOption,
  ToasterService,
  TriggerActionHandler,
} from '../../../../ui-components';
import {
  Action,
  Data,
  dualLanguageCustomValidators,
  FormBuilderComponent,
  FormService,
  FormStatusService,
  RequestFormGroup,
  RequestFromBuilderResponse,
  ShowConditionService,
  TriggerAction,
} from '../../../../form-builder';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { REQUEST_CONTEXT_ADAPTER } from './adapters/request-context.adapter';
import { EmployeeRequestContextAdapter } from './adapters/employee-request-context.adapter';

/** Query params used when starting a child workflow from a parent request. */
export const PARENT_WF_ID_QUERY_PARAM = 'parent_wf_id';
export const PARENT_FIELD_KEY_QUERY_PARAM = 'parent_field_key';

const PARENT_CONTEXT_QUERY_PARAMS = new Set([
  PARENT_WF_ID_QUERY_PARAM,
  PARENT_FIELD_KEY_QUERY_PARAM,
]);

@Component({
  selector: 'lib-request',
  imports: [
    ReactiveFormsModule,
    RequestDetailsHeaderComponent,
    StepperComponent,
    TranslocoPipe,
    FormBuilderComponent,
    ButtonComponent,
    OverlayLoaderComponent,
    SideMenuAssigneeDropdownViewComponent,
    AssigneeTypePipe,
    GroupedButtonComponent,
    ScrollToErrorDirective,
    ActionClassPipe,
    RequestViewActionsComponent,
    AlertInlineComponent,
    ApproveModalComponent,
    RejectModalComponent,
    ReassignModalComponent,
    RejectSendBackModalComponent,
    RejectSendForwardModalComponent,
    HoldResumeModalComponent,
    ChevronDownIconComponent,
  ],
  templateUrl: './request.component.html',
  styleUrl: './request.component.scss',
  changeDetection: ChangeDetectionStrategy.Eager,
  providers: [{ provide: TriggerActionHandler, useExisting: RequestComponent }],
})
export class RequestComponent
  extends DiscussionBase
  implements OnInit, OnDestroy, TriggerActionHandler
{
  readonly EMPLOYEE_ROUTES = EMPLOYEE_ROUTES;

  activatedRoute = inject(ActivatedRoute);
  requestComponentService = inject(RequestComponentService);
  router = inject(Router);
  formService = inject(FormService);
  RequestTypes = RequestTypeEnum;
  transloco = inject(TranslocoService);
  showConditionService = inject(ShowConditionService);
  fb = inject(FormBuilder);
  modalService = inject(ModalServiceService);
  toasterService = inject(ToasterService);
  collaboratorService = inject(CollaboratorService);
  featureFlagsService = inject(FeatureFlagsService);
  workflowAccessKeyService = inject(WorkflowAccessKeyService);
  permissionsService = inject(PermissionsService);
  private readonly destroyRef = inject(DestroyRef);
  private readonly requestContextAdapter =
    inject(REQUEST_CONTEXT_ADAPTER, { optional: true }) ??
    inject(EmployeeRequestContextAdapter);
  protected readonly showCollaboratorsSection = computed(() =>
    this.requestContextAdapter.showCollaboratorsSection()
  );
  protected readonly showSaveAction = signal<boolean>(
    this.requestContextAdapter.showSaveAction()
  );
  protected readonly showRequestViewActions = signal<boolean>(
    this.requestContextAdapter.showRequestViewActions()
  );
  protected readonly showHoldResumeActions = signal<boolean>(
    this.requestContextAdapter.showHoldResumeActions()
  );
  appPermissions = AppPermissions;
  /*******************************************/

  requestDetailsResponse = signal<RequestDetailsResponse>({
    data: requestDetailsEmptyData,
  });
  offCanvasRequestDetailsResponse = signal<RequestDetailsResponse>({
    data: requestDetailsEmptyData,
  });

  isRequestHeaderUiEnhancementEnabled = signal<boolean>(
    this.featureFlagsService.getFeatureFlagValue(
      FEATURE_FLAGS.WORKFLOWS.workflow_header_ui_enhancement
    )
  );

  readonly parentAndChildApproachFlag = signal(
    this.featureFlagsService.getFeatureFlagValue(
      FEATURE_FLAGS.WORKFLOWS.parent_and_child_approach
    )
  );
  requestID = 0;
  stepID = 0;
  requestType = signal<string>(RequestTypeEnum.VIEW);
  stepperOption = signal<StepperOption[]>([]);
  /******** ui ******/
  isDisabled = signal<boolean>(false);
  isSaved = signal<boolean>(false);
  isLoading = signal<boolean>(false);
  isStepperCollapsed = signal<boolean>(true);
  showID = signal<boolean>(false);
  showStatus = signal<boolean>(false);
  showDescription = signal<boolean>(true);
  showCategory = signal<boolean>(true);
  projectId = signal<number>(0);
  userType = signal<UserType | null>(null);
  userId = signal<string | null>(null);
  // hold alert
  showHoldAlert = signal<boolean>(false);
  holdAlertData = signal<AlertData>({
    title: this.transloco.translate('request_screen_hold_alert_title'),
    description: this.transloco.translate(
      'request_screen_hold_alert_description'
    ),
    status: 'warning',
  });

  /************** collaborators **************/
  isLoadingCollaborators = signal<boolean>(false);
  totalCollaboratorsPages = signal<number>(0);
  collaboratorsCurrentPage = signal<number>(1);
  totalCollaboratorsItems = signal<number>(0);
  collaborators = signal<Option[]>([]);
  rawCollaborators = signal<Option[]>([]);
  savedCollaboratorIds = signal<number[]>([]);

  /************** formBuilder **************/
  uploadFileUrls =
    this.activatedRoute.snapshot.data[UPLOAD_FILES_URL_KEY] || uploadFileUrls;
  formStatusService = inject(FormStatusService);
  requestFormGroups = signal<RequestFormGroup[]>([]);
  requestForm: FormGroup = new FormGroup({});
  formAction = signal<Action[]>([]);
  /** Raw API actions from form-builder (used for renamed TRIGGER submit label). */
  readonly triggerActions = signal<Action[]>([]);
  submitDataAction = signal<Action>({
    value: RequestActionEnum.CONTRIBUTE,
    label: this.transloco.translate(
      'contribute_request_screen_submit_data_button_label'
    ),
    color: 'primary',
  });
  saveAction = signal<Action>({
    value: RequestActionEnum.SAVE,
    label: this.transloco.translate('request_screen_save_button'),
    color: 'secondary',
  });
  onHoldAction = signal<Action>({
    value: RequestActionEnum.HOLD,
    label: this.transloco.translate('request_screen_on_hold_button'),
    color: '',
  });
  resumeAction = signal<Action>({
    value: RequestActionEnum.RESUME,
    label: this.transloco.translate('request_screen_resume_button'),
    color: 'primary',
  });

  readonly triggerSubmitLabel = computed(() => {
    const defaultLabel = this.transloco.translate(
      'assign_items_modal_submit_button'
    );
    return this.triggerActions()[0]?.label ?? defaultLabel;
  });

  requestTypeEnum = RequestTypeEnum;
  requestActionEnum = RequestActionEnum;
  navigationTimer = signal<any>(null);
  // Snapshot of initial raw form values for computing changed-only payloads
  private initialFormValues: any = {};

  displayActions = computed<(Action | Action[])[]>(() => {
    const actions = this.formAction();
    const rejectActions = actions.filter(
      action => action.group?.value === RequestActionEnum.REJECT
    );

    if (
      this.showHoldResumeActions() &&
      this.requestDetailsResponse().data?.state?.value &&
      this.permissionsService.checkPermission(
        this.appPermissions.workflow.hold,
        this.requestDetailsResponse().data.permissions
      )
    ) {
      this.addOnHoldOrResumeAction(actions);
    }

    if (rejectActions.length <= 1) {
      return actions;
    }

    // Build result: preserve original order but group all rejects together
    const result: (Action | Action[])[] = [];
    let rejectGroupAdded = false;

    for (const action of actions) {
      if (action.group?.value === RequestActionEnum.REJECT) {
        if (!rejectGroupAdded) {
          result.push(rejectActions);
          rejectGroupAdded = true;
        }
      } else {
        // Add non-reject actions as individual objects
        result.push(action);
      }
    }

    return result;
  });

  isActionArray(item: Action | Action[]): item is Action[] {
    return Array.isArray(item);
  }

  // actions modals
  holdModalTemplate = viewChild<TemplateRef<any>>('holdModalTemplate');
  resumeModalTemplate = viewChild<TemplateRef<any>>('resumeModalTemplate');
  reassignModalTemplate = viewChild<TemplateRef<any>>('reassignModalTemplate');
  sendBackModalTemplate = viewChild<TemplateRef<any>>('sendBackModalTemplate');
  sendForwardModalTemplate = viewChild<TemplateRef<any>>(
    'sendForwardModalTemplate'
  );
  approveModalTemplate = viewChild<TemplateRef<any>>('approveModalTemplate');
  rejectModalTemplate = viewChild<TemplateRef<any>>('rejectModalTemplate');
  scrollToError = viewChild.required(ScrollToErrorDirective);

  canDeactivate = signal<boolean>(true);

  private readonly destroy$ = new Subject<void>();

  constructor() {
    super();
    effect(() => {
      if (!this.modalService.modalContent().isOpen) {
        this.canDeactivate.set(true);
      }
    });
  }

  ngOnInit(): void {
    this.handleRouteParams();
  }

  handleRouteParams() {
    this.subscription.add(
      this.activatedRoute.params.subscribe(params => {
        this.requestType.set(params['requestAction']);
        this.requestID = +params['requestId'];
        this.stepID = +params['stepId'];
        this.projectId.set(+params['projectId']);
        this.flowId.set(params['flowId']);
        this.userType.set(params['userType'] as UserType);
        this.userId.set(params['userId']);

        if (
          this.stepID &&
          this.flowId() &&
          this.userId() &&
          (this.userType() === UserType.SUPER_MANAGER ||
            this.userType() === UserType.DIRECT_MANAGER)
        ) {
          this.requestType.set(this.RequestTypes.ACTION);
          this.getAuthorizationKey();
          this.handleRefreshAuthorizationKey();
        } else if (
          this.requestID &&
          this.requestType() == this.RequestTypes.TRIGGER
        ) {
          this.showID.set(false);
          this.showStatus.set(false);
          this.getPermissionsAuthorizationKey(params['requestId']);
          this.handleRefreshPermissionsAuthorizationKey();
        } else if (
          this.stepID &&
          (this.requestType() == this.RequestTypes.ACTION ||
            this.requestType() == this.RequestTypes.CONTRIBUTE)
        ) {
          this.getAuthorizationKey();
          this.handleRefreshAuthorizationKey();
        } else {
          this.cancel();
        }
      })
    );
  }

  getAuthorizationKey() {
    const descendant_user_id =
      this.userType() === UserType.DIRECT_MANAGER ? this.userId() : null;
    this.subscription.add(
      this.requestContextAdapter
        .getStepAuthorizationKey(this.stepID, descendant_user_id)
        .subscribe({
          next: (res: { data: { access_key: string } }) => {
            const accessKey = res.data.access_key;
            sessionStorage.setItem(AppStorage.accessKey, accessKey);
            this.handleStepIdExists();
            this.listenToVisibilityChanges();
          },
        })
    );
  }

  handleRefreshAuthorizationKey() {
    timer(
      this.workflowAccessKeyService.refreshingAccessKeyTime,
      this.workflowAccessKeyService.refreshingAccessKeyTime
    )
      .pipe(
        switchMap(() =>
          this.requestContextAdapter.refreshStepAuthorizationKey(this.stepID)
        ),
        takeUntil(this.destroy$)
      )
      .subscribe();
  }

  getPermissionsAuthorizationKey(requestId: string) {
    this.subscription.add(
      this.workflowAccessKeyService
        .getPermissionsAccessKey(requestId)
        .subscribe({
          next: (res: AccessKeyResponse) => {
            const accessKey = res.data.access_key;
            sessionStorage.setItem(AppStorage.accessKey, accessKey);
            this.getRequestFromBuilder(requestId);
            this.getRequestDetails(requestId);
          },
        })
    );
  }

  handleRefreshPermissionsAuthorizationKey() {
    timer(
      this.workflowAccessKeyService.refreshingAccessKeyTime,
      this.workflowAccessKeyService.refreshingAccessKeyTime
    )
      .pipe(
        switchMap(() =>
          this.workflowAccessKeyService.refreshPermissionsAccessKey(
            this.requestID
          )
        ),
        takeUntil(this.destroy$)
      )
      .subscribe();
  }

  handleStepIdExists() {
    this.showID.set(true);
    this.showCategory.set(true);
    this.showDescription.set(true);
    this.getFlowSteps(this.flowId() ?? '');
    this.getFormSection(this.stepID?.toString() ?? '');
    if (this.showCollaboratorsSection()) {
      this.getAssignedCardList({ currentPage: 1, search: '' });
    }
  }

  ngOnDestroy(): void {
    this.closeModal();
    this.formStatusService.isSubmitted.set(false);
    clearTimeout(this.navigationTimer());
    this.subscription.unsubscribe();
    this.destroy$.next();
    this.destroy$.complete();
    this.pusherService.unsubscribe(this.channelName());
    this.isPusherChannelActive.set(false);
    sessionStorage.removeItem(AppStorage.accessKey);
  }

  /************************ take action *************************/
  getFlowSteps(flowId: string | number) {
    this.isLoading.set(true);
    this.subscription.add(
      this.requestContextAdapter.getFlowSteps(flowId).subscribe({
        next: (res: any) => {
          this.requestDetailsResponse.set(res);

          this.flowId.set(res.data.id);
          if (this.showRequestViewActions()) {
            this.getFlowStepsDetails(this.flowId());
          }
          this.subscribeToNewComment();
          this.isPusherChannelActive.set(true);
          this.showUnreadMessagesIndicator.set(res.data.has_unread_comments);

          this.requestDetailsResponse.update(x => {
            x.data.positions?.forEach(p => {
              p.state = this.requestService.getPositionState(p);
            });
            return x;
          });

          this.stepperOption.set(
            this.requestComponentService.drawStepper(
              this.requestDetailsResponse()
            )
          );

          if (
            this.requestDetailsResponse().data?.state?.value ===
            StepStatusEnum.ON_HOLD
          ) {
            this.showHoldAlert.set(true);
          }

          this.isLoading.set(false);
        },
        error: (error: any) => {
          this.handleRequestError(error);
        },
      })
    );
  }

  getFlowStepsByStepId(stepId: string | number) {
    this.isLoading.set(true);
    this.subscription.add(
      this.requestService.getStepDetails(stepId).subscribe({
        next: (res: any) => {
          const flowId = res.data.flow.id;
          this.flowId.set(flowId);
          this.getFlowSteps(flowId);
        },
        error: (error: any) => {
          this.handleRequestError(error);
        },
      })
    );
  }

  getFlowStepsDetails(flowId: string | number) {
    this.subscription.add(
      this.requestContextAdapter.getFlowStepsDetails(flowId).subscribe({
        next: (res: any) => {
          this.offCanvasRequestDetailsResponse.set(res);

          this.offCanvasRequestDetailsResponse.update(x => {
            x.data.positions?.forEach(p => {
              p.state = this.requestService.getPositionState(p);
            });
            return x;
          });
        },
        error: (error: any) => {
          this.handleRequestError(error);
        },
      })
    );
  }

  getFormSection(stepID: string) {
    const formSections$ =
      this.requestType() === this.RequestTypes.CONTRIBUTE
        ? this.requestService.getContributorFormSections(stepID)
        : this.requestContextAdapter.getFormSection(stepID);
    this.subscription.add(
      formSections$.subscribe({
        next: (res: { data: Data }) => {
          this.requestFormGroups.set(res.data.sections);

          this.formAction.set(res.data.actions ?? []);

          this.requestForm = this.requestComponentService.buildRequestForm(
            this.requestFormGroups()
          );

          // Capture initial values snapshot for change detection
          this.subscription.add(
            of(null)
              .pipe(delay(500))
              .subscribe(() => {
                this.initialFormValues = this.requestForm.getRawValue();
              })
          );
        },
      })
    );
  }

  /**
   * Parent/child Start CTA only. Uses form project_id so form-sections
   * receives default_values[project_id].
   */
  handle(triggerAction: TriggerAction, parentFieldKey: string | null): void {
    const templateId = this.getTriggerTemplateId(triggerAction);
    const extraQueryParams = triggerAction?.extra_data;
    if (!this.parentAndChildApproachFlag() || !templateId) {
      return;
    }

    const parentWfId = this.requestDetailsResponse().data?.id || this.requestID;
    const queryParams: Record<string, string | number> = {
      [PARENT_WF_ID_QUERY_PARAM]: parentWfId,
    };

    if (parentFieldKey) {
      queryParams[PARENT_FIELD_KEY_QUERY_PARAM] = parentFieldKey;
    }

    // add extra query params to the query params object
    if (extraQueryParams) {
      Object.entries(extraQueryParams).forEach(([key, value]) => {
        if (key !== 'template_id') {
          let queryParamValue = value;
          if (typeof value === 'string' && value.startsWith(':')) {
            queryParamValue = this.requestForm?.get(value.slice(1))?.value;
          }
          queryParams[key] = queryParamValue;
        }
      });
    }

    this.router.navigate(
      [
        this.requestService.navigationPath(
          `${EMPLOYEE_ROUTES.REQUESTS.DETAILS}/${templateId}/${RequestTypeEnum.TRIGGER}`
        ),
      ],
      { queryParams }
    );
  }

  private getTriggerTemplateId(
    triggerAction: TriggerAction | null | undefined
  ): number | undefined {
    return triggerAction?.extra_data?.template_id;
  }

  /**
   * Adds parent context to the trigger() payload when opening a child workflow.
   * Both values come from query params set when navigating from the parent
   * form-builder field (create_action + parent_field_key).
   */
  private applyParentContextToTriggerPayload(payload: {
    parent_flow_id?: number;
    parent_field_key?: string;
  }): void {
    const queryParams = this.activatedRoute.snapshot?.queryParams ?? {};
    const parentFlowIdRaw = queryParams[PARENT_WF_ID_QUERY_PARAM];
    const parentFlowId = parentFlowIdRaw ? Number(parentFlowIdRaw) : NaN;

    if (!Number.isNaN(parentFlowId)) {
      payload.parent_flow_id = parentFlowId;
    }

    const parentFieldKey = queryParams[PARENT_FIELD_KEY_QUERY_PARAM];
    if (parentFieldKey) {
      payload.parent_field_key = parentFieldKey;
    }
  }

  /********************** trigger  ****************************/
  getRequestDetails(requestId: string) {
    this.subscription.add(
      this.requestService
        .getRequestDetails(requestId)
        .subscribe((response: RequestDetailsResponse) => {
          this.requestDetailsResponse.set(response);

          this.requestDetailsResponse.update(x => {
            x.data.current_position = 1;
            return x;
          });

          this.stepperOption.set(
            this.requestComponentService.drawStepper(
              this.requestDetailsResponse()
            )
          );
        })
    );
  }

  getRequestFromBuilder(requestID: string) {
    this.isLoading.set(true);
    const queryParams = this.getDefaultValueQueryParams();
    this.subscription.add(
      this.requestService
        .getRequestFromBuilder(requestID, this.projectId(), queryParams)
        .subscribe({
          next: (res: RequestFromBuilderResponse) => {
            this.requestFormGroups.set(res.data.sections);

            const actions = [...(res.data.actions ?? [])];
            this.triggerActions.set(actions);
            this.formAction.set(this.addSaveActionToFormActions([...actions]));

            this.requestForm = this.requestComponentService.buildRequestForm(
              this.requestFormGroups()
            );
            // Capture initial values snapshot for change detection
            this.initialFormValues = this.requestForm.getRawValue();
            this.isLoading.set(false);
          },
          error: (error: any) => {
            this.handleRequestError(error);
          },
        })
    );
  }

  /** Excludes parent-child context params so they are not sent as default_values. */
  private getDefaultValueQueryParams(): Record<string, string> {
    const queryParams = this.activatedRoute.snapshot?.queryParams ?? {};
    return Object.fromEntries(
      Object.entries(queryParams).filter(
        ([key]) => !PARENT_CONTEXT_QUERY_PARAMS.has(key)
      )
    );
  }

  /** A single-file media field (`multiple` is null) submits its value as a number instead of an array. */
  private convertSingleMediaFieldValues(formData: Record<string, unknown>) {
    this.requestFormGroups().forEach(group => {
      group.fields.forEach(field => {
        if (
          field.type !== this.requestComponentService.inputTypesEnum.MEDIA ||
          field.multiple
        )
          return;
        const value = formData[field.key];
        if (Array.isArray(value)) {
          formData[field.key] = value[0] ?? null;
        }
      });
    });
  }

  /********************* sent form ****************************/

  cancel() {
    of(null)
      .pipe(delay(2000))
      .subscribe(() => {
        history.back();
      });
  }

  handleRequestError(error: any): void {
    if (error.status === 403) {
      this.router.navigate([this.requestContextAdapter.getHomePath()]);
    } else {
      this.cancel();
    }
    this.isLoading.set(false);
  }

  trigger() {
    if (this.requestForm.invalid) {
      // mark form controls as touched
      this.markFormControlsAsTouched();

      this.formStatusService.isSubmitted.set(true);
      this.subscription.add(
        of(null)
          .pipe(delay(1))
          .subscribe(() => {
            this.scrollToError().scrollToFirstError();
            this.toasterService.add(
              this.transloco.translate('scroll_to_error_message_label'),
              '',
              '',
              'error',
              true,
              true
            );
          })
      );
      return;
    }
    this.isDisabled.set(true);

    this.formService.syncDualLangControls(this.requestForm);

    // Use the form service to format dual language fields
    const formData = this.requestForm.value;

    this.convertSingleMediaFieldValues(formData);

    const payload: {
      user_inputs: typeof formData;
      collaborator_ids?: number[];
      parent_flow_id?: number;
      parent_field_key?: string;
    } = { user_inputs: formData };

    if (this.showCollaboratorsSection()) {
      payload.collaborator_ids = this.savedCollaboratorIds();
    }

    if (this.parentAndChildApproachFlag()) {
      this.applyParentContextToTriggerPayload(payload);
    }

    this.subscription.add(
      this.requestService.triggerRequest(payload, this.requestID).subscribe({
        next: () => {
          this.isDisabled.set(false);
          this.navigationTimer.set(
            setTimeout(() => {
              this.router.navigate([
                this.requestService.navigationPath(
                  `${EMPLOYEE_ROUTES.REQUESTS.LIST}/${this.requestID}`
                ),
              ]);
            }, 100)
          );
          this.formStatusService.isSubmitted.set(false);
        },
        error: error => {
          this.isDisabled.set(false);
          this.formService.addControlErrors(error, this.requestForm);
          this.formStatusService.isSubmitted.set(false);
          this.subscription.add(
            of(null)
              .pipe(delay(1))
              .subscribe(() => {
                this.scrollToError().scrollToFirstError(false);
                this.toasterService.add(
                  error.message ? error.message : error.error?.message,
                  '',
                  '',
                  'error',
                  true,
                  true
                );
              })
          );
        },
      })
    );
  }

  handleTakenAction(action: string) {
    const requiresValidation =
      action === RequestActionEnum.APPROVE ||
      action === RequestActionEnum.SENDFORWARD ||
      action === RequestActionEnum.COMPLETE ||
      action === RequestActionEnum.SUBMIT;

    if (requiresValidation && this.requestForm.invalid) {
      // mark form controls as touched
      this.markFormControlsAsTouched();
      this.formStatusService.isSubmitted.set(true);
      this.scrollToError().scrollToFirstError();
      return;
    }

    switch (action) {
      case RequestActionEnum.APPROVE:
        this.openApproveModal();
        break;
      case RequestActionEnum.REJECT:
        this.openRejectModal();
        break;
      case RequestActionEnum.SENDFORWARD:
        this.openSendForwardModal();
        break;
      case RequestActionEnum.SENDBACK:
        this.openSendBackModal();
        break;
      case RequestActionEnum.REASSIGN:
        this.openReassignModal();
        break;
      case RequestActionEnum.SAVE:
        this.save();
        break;
      case RequestActionEnum.HOLD:
        this.openHoldModal();
        break;
      case RequestActionEnum.RESUME:
        this.openResumeModal();
        break;
      default:
        this.takeStepAction(action);
        break;
    }
  }

  takeStepAction(action: string, actionData?: any) {
    if (this.isDisabled()) {
      return;
    } else {
      this.isDisabled.set(true);
    }

    this.formService.syncDualLangControls(this.requestForm);

    // Use the form service to format fields
    const formData = this.requestComponentService.prepareRequestFormData(
      this.requestForm.value,
      this.requestFormGroups()
    );

    // to add comment key to payload here
    const payload: any = { user_inputs: formData, action: action };
    let isFormValidWithoutRequired = true;
    if (
      action === RequestActionEnum.REASSIGN ||
      action === RequestActionEnum.CONTRIBUTE
    ) {
      isFormValidWithoutRequired = this.submitWithoutRequiredValidation();
      payload.user_inputs = this.buildChangedUserInputs();
    }

    if (actionData) {
      payload.action_data = {
        ...actionData,
        comment: { en: actionData.comment },
      };
    }

    if (isFormValidWithoutRequired) {
      if (action === RequestActionEnum.CONTRIBUTE) {
        this.contributorTakeAction(payload.user_inputs);
      } else if (this.userType() === UserType.DIRECT_MANAGER) {
        this.directManagerTakeAction(payload);
      } else if (this.userType() === UserType.SUPER_MANAGER) {
        this.superManagerTakeAction(payload);
      } else {
        this.assigneeTakeAction(payload);
      }
    } else {
      this.isDisabled.set(false);
    }
  }

  assigneeTakeAction(payload: any) {
    this.subscription.add(
      this.requestContextAdapter
        .takeStepAction(payload, this.stepID)
        .subscribe({
          next: () => {
            this.handleTakeActionSuccess();
          },
          error: error => {
            this.handleTakeActionError(error);
          },
        })
    );
  }

  directManagerTakeAction(payload: any) {
    this.subscription.add(
      this.requestService
        .takeActionDirectManager(payload, this.userId() ?? '', this.stepID)
        .subscribe({
          next: () => {
            this.handleTakeActionSuccess();
          },
          error: error => {
            this.handleTakeActionError(error);
          },
        })
    );
  }

  superManagerTakeAction(payload: any) {
    this.subscription.add(
      this.requestService
        .takeActionSuperManager(payload, this.stepID, this.userId() ?? '')
        .subscribe({
          next: () => {
            this.handleTakeActionSuccess();
          },
          error: error => {
            this.handleTakeActionError(error);
          },
        })
    );
  }

  contributorTakeAction(payload: any) {
    this.requestService
      .takeActionContributor(payload, this.stepID)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: () => {
          this.handleTakeActionSuccess();
        },
        error: error => {
          this.handleTakeActionError(error);
        },
      });
  }

  handleTakeActionSuccess() {
    this.isDisabled.set(false);
    this.formStatusService.isSubmitted.set(false);
    this.closeModal();

    const navigation = this.requestContextAdapter.getAfterActionNavigation({
      requestType: this.requestType(),
      userType: this.userType(),
    });

    if (navigation.queryParams) {
      this.router.navigate(navigation.commands, {
        queryParams: navigation.queryParams,
      });
    } else {
      this.router.navigate(navigation.commands);
    }
  }

  handleTakeActionError(error: any) {
    this.closeModal();
    this.isDisabled.set(false);
    this.formService.addControlErrors(error, this.requestForm);
    this.formStatusService.isSubmitted.set(false);
    this.toasterService.add(
      error.message ? error.message : error.error?.message,
      '',
      '',
      'error',
      true,
      true
    );
    this.scrollToError().scrollToFirstError(false);
  }

  holdRequest(actionData: any) {
    if (this.isDisabled()) {
      return;
    } else {
      this.isDisabled.set(true);
    }

    const payload: any = {
      comment: { en: actionData.comment },
    };

    this.subscription.add(
      this.requestService
        .holdRequest(payload, this.flowId())
        .pipe(
          finalize(() => {
            this.isDisabled.set(false);
          })
        )
        .subscribe({
          next: () => {
            this.handleTakeActionSuccess();
          },
        })
    );
  }

  resumeRequest(actionData: any) {
    if (this.isDisabled()) {
      return;
    } else {
      this.isDisabled.set(true);
    }

    const payload: any = {
      comment: { en: actionData.comment },
    };

    this.subscription.add(
      this.requestService
        .resumeRequest(payload, this.flowId())
        .pipe(
          finalize(() => {
            this.isDisabled.set(false);
          })
        )
        .subscribe({
          next: () => {
            this.handleTakeActionSuccess();
          },
        })
    );
  }

  /************** modals **************/
  openReassignModal() {
    this.canDeactivate.set(false);
    this.modalService.modalContent.set({
      isOpen: true,
      hasCloseIcon: true,
      title: this.transloco.translate('request_screen_reassign_modal_title'),
      customizeContent: this.reassignModalTemplate(),
      width: '45.375',
      canNotCloseFromOutside: true,
      modalClass: 'overflow-y-auto',
    });
  }

  openSendBackModal() {
    this.canDeactivate.set(false);
    this.modalService.modalContent.set({
      isOpen: true,
      hasCloseIcon: true,
      title: this.transloco.translate('request_screen_send_back_modal_title'),
      customizeContent: this.sendBackModalTemplate(),
      width: '45.375',
      canNotCloseFromOutside: true,
      modalClass: 'overflow-y-auto',
    });
  }

  openSendForwardModal() {
    this.canDeactivate.set(false);
    this.modalService.modalContent.set({
      isOpen: true,
      hasCloseIcon: true,
      title: this.transloco.translate(
        'request_screen_send_forward_modal_title'
      ),
      customizeContent: this.sendForwardModalTemplate(),
      width: '45.375',
      canNotCloseFromOutside: true,
      modalClass: 'overflow-y-auto',
    });
  }

  openApproveModal() {
    this.canDeactivate.set(false);
    this.modalService.modalContent.set({
      isOpen: true,
      hasCloseIcon: true,
      title: this.transloco.translate('request_screen_approve_modal_title'),
      customizeContent: this.approveModalTemplate(),
      width: '45.375',
      canNotCloseFromOutside: true,
      modalClass: 'overflow-y-auto',
    });
  }

  openRejectModal() {
    this.canDeactivate.set(false);
    this.modalService.modalContent.set({
      isOpen: true,
      hasCloseIcon: true,
      title: this.transloco.translate('request_screen_reject_modal_title'),
      customizeContent: this.rejectModalTemplate(),
      width: '45.375',
      canNotCloseFromOutside: true,
      modalClass: 'overflow-y-auto',
    });
  }

  openHoldModal() {
    this.canDeactivate.set(false);
    this.modalService.modalContent.set({
      isOpen: true,
      hasCloseIcon: true,
      title: this.transloco.translate('request_screen_hold_modal_title'),
      customizeContent: this.holdModalTemplate(),
      width: '45.375',
      canNotCloseFromOutside: true,
      modalClass: 'overflow-y-auto',
    });
  }

  openResumeModal() {
    this.canDeactivate.set(false);
    this.modalService.modalContent.set({
      isOpen: true,
      hasCloseIcon: true,
      title: this.transloco.translate('request_screen_resume_modal_title'),
      customizeContent: this.resumeModalTemplate(),
      width: '45.375',
      canNotCloseFromOutside: true,
      modalClass: 'overflow-y-auto',
    });
  }

  submitWithoutRequiredValidation() {
    // Temporarily remove required validators for save only
    this.removeRequiredValidators();
    this.requestForm.updateValueAndValidity();

    if (this.requestForm.invalid) {
      this.markFormControlsAsTouched();

      this.toasterService.add(
        this.transloco.translate('scroll_to_error_message_label'),
        '',
        '',
        'error',
        true,
        true
      );
      this.subscription.add(
        of(null)
          .pipe(delay(1500))
          .subscribe(() => {
            this.scrollToError().scrollToFirstError(false);
          })
      );

      this.subscription.add(
        of(null)
          .pipe(delay(2000))
          .subscribe(() => {
            this.delayedRestoreRequiredValidators();
          })
      );
      return false;
    }

    return true;
  }

  save() {
    const isValid = this.submitWithoutRequiredValidation();

    if (!isValid) return;

    this.formService.syncDualLangControls(this.requestForm);
    this.isSaved.set(true);
    this.subscription.add(
      this.requestContextAdapter
        .saveRequest(this.buildChangedUserInputs(), this.stepID)
        .subscribe({
          next: () => this.handleSaveSuccess(),
          error: error => this.handleSaveError(error),
        })
    );
  }

  handleSaveSuccess() {
    this.isSaved.set(false);
    this.toasterService.add(
      this.transloco.translate('request_screen_save_success_message'),
      '',
      '',
      'success'
    );

    this.formService.clearApiErrors(this.requestForm);
    this.restoreRequiredValidators();
  }

  handleSaveError(error: any) {
    this.isSaved.set(false);
    this.formService.addControlErrors(error, this.requestForm);
    this.toasterService.add(
      error.message ? error.message : error.error?.message,
      '',
      '',
      'error',
      true,
      true
    );

    this.subscription.add(
      of(null)
        .pipe(delay(1500))
        .subscribe(() => {
          this.scrollToError().scrollToFirstError(false);
        })
    );

    this.subscription.add(
      of(null)
        .pipe(delay(2000))
        .subscribe(() => {
          this.delayedRestoreRequiredValidators();
        })
    );
  }

  handleSaveErrorScroll() {
    this.scrollToError().scrollToFirstError(false);

    this.delayedRestoreRequiredValidators();
  }

  delayedRestoreRequiredValidators() {
    of(null)
      .pipe(delay(1000))
      .subscribe(() => {
        this.restoreRequiredValidators();
        this.requestForm.updateValueAndValidity();
      });
  }

  closeModal() {
    this.canDeactivate.set(true);
    this.modalService.modalContent.set({
      isOpen: false,
    });
  }

  addSaveActionToFormActions(formActions: Action[]) {
    const isSaveActionExists = formActions.some(
      action => action.value === RequestActionEnum.SAVE
    );
    const primaryActionIndex = formActions.findIndex(
      action => action.color === 'primary'
    );
    if (!isSaveActionExists) {
      const insertIndex =
        primaryActionIndex === -1 ? formActions.length : primaryActionIndex;
      formActions.splice(insertIndex, 0, this.saveAction());
    }
    return formActions;
  }

  addOnHoldOrResumeAction(formActions: Action[]) {
    const isAddOnHoldOrResumeActionExists = this.formAction().some(
      action =>
        action.value === RequestActionEnum.HOLD ||
        action.value === RequestActionEnum.RESUME
    );
    if (!isAddOnHoldOrResumeActionExists) {
      if (
        this.requestDetailsResponse().data?.state?.value ===
        StepStatusEnum.INPROGRESS
      ) {
        formActions.unshift(this.onHoldAction());
      } else if (
        this.requestDetailsResponse().data?.state?.value ===
        StepStatusEnum.ON_HOLD
      ) {
        formActions.unshift(this.resumeAction());
      }
    }
    return formActions;
  }

  // Remove only required validators for save flow without touching other validators
  private removeRequiredValidators() {
    const groups = this.requestFormGroups();
    groups.forEach(group => {
      group.fields.forEach(field => {
        if (!field.is_required) return;

        if (
          field.type ===
            this.requestComponentService.inputTypesEnum
              .DUAL_LANGUAGE_TEXT_INPUT_FIELD ||
          field.type ===
            this.requestComponentService.inputTypesEnum
              .DUAL_LANGUAGE_TEXT_AREA_FIELD ||
          field.type ===
            this.requestComponentService.inputTypesEnum
              .RICH_EDITOR_DUAL_LANGUAGE_TEXT_AREA_FIELD
        ) {
          const enControl = this.requestForm.get(`${field.key}.en`);
          const arControl = this.requestForm.get(`${field.key}.ar`);
          const parent_control = this.requestForm.get(field.key) as FormGroup;

          enControl?.clearValidators();
          enControl?.updateValueAndValidity({
            emitEvent: false,
            onlySelf: true,
          });

          arControl?.clearValidators();
          arControl?.updateValueAndValidity({
            emitEvent: false,
            onlySelf: true,
          });

          parent_control.clearValidators();
          parent_control.updateValueAndValidity({
            emitEvent: false,
            onlySelf: true,
          });
        } else if (
          field.type ===
            this.requestComponentService.inputTypesEnum.GROUP_INPUT_FIELD ||
          field.type ===
            this.requestComponentService.inputTypesEnum.TABLE_INPUT_FIELD
        ) {
          const formArray: any = this.requestForm.get(field.key);
          formArray?.clearValidators();
          formArray?.updateValueAndValidity({ onlySelf: true });
        } else {
          const control = this.requestForm.get(field.key);
          control?.clearValidators();
          control?.updateValueAndValidity({ onlySelf: true, emitEvent: false });
        }
      });
    });
  }

  // Restore required validators after save flow so trigger() remains unaffected
  private restoreRequiredValidators() {
    const groups = this.requestFormGroups();
    groups.forEach(group => {
      group.fields.forEach(field => {
        if (!field.is_required) return;

        if (
          field.type ===
            this.requestComponentService.inputTypesEnum
              .DUAL_LANGUAGE_TEXT_INPUT_FIELD ||
          field.type ===
            this.requestComponentService.inputTypesEnum
              .DUAL_LANGUAGE_TEXT_AREA_FIELD ||
          field.type ===
            this.requestComponentService.inputTypesEnum
              .RICH_EDITOR_DUAL_LANGUAGE_TEXT_AREA_FIELD
        ) {
          const enControl = this.requestForm.get(`${field.key}.en`);
          const arControl = this.requestForm.get(`${field.key}.ar`);
          const parent_control = this.requestForm.get(field.key) as FormGroup;

          parent_control.setValidators([
            dualLanguageCustomValidators.atLeastOneLangRequired,
          ]);

          enControl?.updateValueAndValidity({ onlySelf: true });
          arControl?.updateValueAndValidity({ onlySelf: true });
          parent_control.updateValueAndValidity({ onlySelf: true });
        } else if (
          field.type ===
          this.requestComponentService.inputTypesEnum.SINGLE_CHECK_BOX
        ) {
          const control = this.requestForm.get(field.key);
          control?.setValidators([Validators.requiredTrue]);
          control?.updateValueAndValidity();
        } else if (
          field.type ===
            this.requestComponentService.inputTypesEnum.GROUP_INPUT_FIELD ||
          field.type ===
            this.requestComponentService.inputTypesEnum.TABLE_INPUT_FIELD
        ) {
          const formArray: any = this.requestForm.get(field.key);
          formArray?.setValidators([Validators.required]);
          formArray?.updateValueAndValidity({ onlySelf: true });
        } else {
          const control = this.requestForm.get(field.key);
          control?.setValidators([Validators.required]);
          control?.updateValueAndValidity({ onlySelf: true, emitEvent: false });
        }
      });
    });
  }

  // Build a changed-only user_inputs object formatted for dual language fields
  private buildChangedUserInputs(): any {
    const currentRaw = this.requestForm.getRawValue();
    const initialRaw = this.initialFormValues ?? {};

    const formattedCurrent = { ...currentRaw };
    const formattedInitial = { ...initialRaw };

    const diff: any = {};
    Object.keys(formattedCurrent).forEach(key => {
      const currentVal = formattedCurrent[key];
      const initialVal = formattedInitial[key];
      if (
        !this.areValuesEqual(currentVal, initialVal) ||
        this.isInitialValue(initialVal)
      ) {
        diff[key] = currentVal;
      }
    });
    return diff;
  }

  private isInitialValue(initialVal: any): boolean {
    if (Array.isArray(initialVal) && initialVal.length && initialVal[0]) {
      return true;
    } else if (
      initialVal &&
      typeof initialVal === 'object' &&
      Object.prototype.hasOwnProperty.call(initialVal, 'en') &&
      initialVal.en &&
      initialVal.ar
    ) {
      return true;
    } else if (
      !Array.isArray(initialVal) &&
      typeof initialVal !== 'object' &&
      (initialVal !== null || initialVal !== undefined || initialVal !== '')
    ) {
      return true;
    }
    return false;
  }

  private areValuesEqual(a: unknown, b: unknown): boolean {
    try {
      return JSON.stringify(a) === JSON.stringify(b);
    } catch {
      return a === b;
    }
  }

  private markFormControlsAsTouched() {
    Object.keys(this.requestForm.controls).forEach(key => {
      const control = this.requestForm.controls[key];
      if (control.invalid) {
        control.markAsTouched();
        control.markAsDirty();
      }

      if (control instanceof FormArray) {
        control.controls.forEach(arrayControl => {
          if (arrayControl.invalid) {
            arrayControl.markAllAsTouched();
            arrayControl.markAllAsDirty();
          }
        });
      }

      //validate form group controls
      if (control instanceof FormGroup) {
        Object.keys(control.controls).forEach(groupControl => {
          if (control.controls[groupControl].invalid) {
            control.controls[groupControl].markAsTouched();
            control.controls[groupControl].markAsDirty();
          }
        });
      }
    });
  }

  getAssignedCardList(event: { currentPage: number; search: string }) {
    if (!this.showCollaboratorsSection()) {
      return;
    }

    if (this.requestType() === this.requestTypeEnum.TRIGGER) {
      this.searchCollaboratorsOnTrigger(event.search);
      return;
    }

    this.collaboratorsCurrentPage.set(event.currentPage);
    this.isLoadingCollaborators.set(true);
    this.subscription.add(
      this.collaboratorService
        .getCollaborators(
          this.flowId()?.toString() ?? '',
          this.collaboratorsCurrentPage(),
          event.search
        )
        .subscribe({
          next: response =>
            this.handleCollaboratorsSuccessResponse(response, event.search),
          error: () => this.isLoadingCollaborators.set(false),
        })
    );
  }

  handleCollaboratorsSuccessResponse(
    response: ApiResponse<UserList>,
    search: string
  ) {
    this.collaborators.set(
      response.data.map((collaborator: any) => ({
        value: collaborator.id,
        label: collaborator.name,
        image: collaborator.image,
        job_title: collaborator.work_info?.job_title?.name ?? '',
      })) as unknown as Option[]
    );
    if (search === '') {
      this.rawCollaborators.set(this.collaborators());
    }
    this.totalCollaboratorsPages.set(response.meta.last_page);
    this.totalCollaboratorsItems.set(response.meta.total);
    this.isLoadingCollaborators.set(false);
  }

  onCollaboratorItemsChanged(items: Option[]) {
    this.rawCollaborators.set(items);
    this.collaborators.set(items);
    this.totalCollaboratorsItems.set(items.length);
    this.savedCollaboratorIds.set(items.map(item => Number(item.value)));
  }

  private searchCollaboratorsOnTrigger(search: string) {
    const keyword = search.trim().toLowerCase();
    if (!keyword) {
      this.collaborators.set(this.rawCollaborators());
      this.totalCollaboratorsItems.set(this.rawCollaborators().length);
      return;
    }
    const filtered = this.rawCollaborators().filter(c =>
      [c.label, c.job_title]
        .filter(Boolean)
        .some(field => (field as string).toLowerCase().includes(keyword))
    );
    this.collaborators.set(filtered);
    this.totalCollaboratorsItems.set(filtered.length);
  }
}
